import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { cn } from '@/utils/merge';

const fmt = v => (v === null || v === undefined || v === '' ? '—' : `${v}`.replace('.', ','));
const parse = v => {
	const n = Number(`${v ?? ''}`.trim().replace(',', '.'));
	return `${v ?? ''}`.trim() !== '' && Number.isFinite(n) && n >= 0 ? n : null;
};

// One amount: the new area's price from the shop's list, or a custom one.
const PriceRow = ({ label, oldValue, listValue, choice, onChoice, custom, onCustom }) => (
	<div className='flex flex-col gap-1'>
		<div className='text-[14px] font-semibold text-slate-800'>
			{label} <span className='font-normal text-slate-500'>(τώρα: {fmt(oldValue)})</span>
		</div>
		<div className='flex flex-wrap items-center gap-2'>
			{listValue !== null && listValue !== undefined && (
				<button
					type='button'
					onClick={() => onChoice('list')}
					className={cn(
						'rounded-md border px-3 py-1.5 text-[14px]',
						choice === 'list'
							? 'border-[#0f9b9d] bg-[#0f9b9d] text-white'
							: 'border-slate-300 bg-white text-slate-700',
					)}
				>
					{fmt(listValue)} (της περιοχής)
				</button>
			)}
			<input
				inputMode='decimal'
				placeholder='Custom τιμή'
				value={custom}
				onFocus={() => onChoice('custom')}
				onChange={e => {
					onChoice('custom');
					onCustom(e.target.value);
				}}
				className={cn(
					'h-9 w-32 rounded-md border px-2 text-[14px]',
					choice === 'custom' ? 'border-[#0f9b9d]' : 'border-slate-300',
				)}
			/>
		</div>
	</div>
);

// Cities with withAreaPriceConfirm (Θέρμη): changing an order's area asks
// whether the shop charge and rider fee follow the new area — the shop's
// price for it, or a custom value — or stay as they are.
export const AreaPricePrompt = ({
	area,
	withFees,
	withShopFees,
	oldFee,
	oldShopFee,
	listFee,
	listShopFee,
	onConfirm,
	onAreaOnly,
	onCancel,
}) => {
	const hasList = v => v !== null && v !== undefined && v !== '';
	const asText = v => (v === null || v === undefined ? '' : `${v}`.replace('.', ','));
	// The area's price is picked by default, so one click confirms. Without one
	// the custom box starts on the current price, so confirming never fails.
	const [shopChoice, setShopChoice] = useState(hasList(listShopFee) ? 'list' : 'custom');
	const [feeChoice, setFeeChoice] = useState(hasList(listFee) ? 'list' : 'custom');
	const [customShopFee, setCustomShopFee] = useState(
		hasList(listShopFee) ? '' : asText(oldShopFee),
	);
	const [customFee, setCustomFee] = useState(hasList(listFee) ? '' : asText(oldFee));

	const confirm = () => {
		const out = {};
		if (withShopFees) {
			const v = shopChoice === 'list' ? parse(listShopFee) : parse(customShopFee);
			if (v === null) {
				toast.error('Η χρέωση καταστήματος πρέπει να είναι νούμερο');
				return;
			}
			out.shopFee = v;
		}
		if (withFees) {
			const v = feeChoice === 'list' ? parse(listFee) : parse(customFee);
			if (v === null) {
				toast.error('Η προμήθεια πρέπει να είναι νούμερο');
				return;
			}
			out.fee = v;
		}
		onConfirm(out);
	};

	return (
		<div className='fixed inset-0 z-[140000000] flex items-center justify-center bg-black/30 p-4'>
			<div className='flex w-full max-w-md flex-col gap-4 rounded-xl bg-white p-5 shadow-xl'>
				<div className='text-[16px] font-semibold text-slate-800'>Αλλαγή περιοχής σε «{area}»</div>
				<div className='text-[14px] text-slate-600'>Να αλλάξουν και οι τιμές της παραγγελίας;</div>
				{withShopFees && (
					<PriceRow
						label='Χρέωση καταστ.'
						oldValue={oldShopFee}
						listValue={listShopFee}
						choice={shopChoice}
						onChoice={setShopChoice}
						custom={customShopFee}
						onCustom={setCustomShopFee}
					/>
				)}
				{withFees && (
					<PriceRow
						label='Προμήθεια'
						oldValue={oldFee}
						listValue={listFee}
						choice={feeChoice}
						onChoice={setFeeChoice}
						custom={customFee}
						onCustom={setCustomFee}
					/>
				)}
				<div className='flex flex-wrap justify-end gap-2 pt-1'>
					<button
						type='button'
						onClick={onCancel}
						className='rounded-md px-3 py-2 text-[14px] text-slate-500'
					>
						Άκυρο
					</button>
					<button
						type='button'
						onClick={onAreaOnly}
						className='rounded-md border border-slate-300 px-3 py-2 text-[14px] text-slate-700'
					>
						Μόνο περιοχή
					</button>
					<button
						type='button'
						onClick={confirm}
						className='rounded-md bg-[#0f9b9d] px-3 py-2 text-[14px] text-white'
					>
						Αλλαγή και τιμών
					</button>
				</div>
			</div>
		</div>
	);
};
